import { FC, ReactNode, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  InputAdornment,
  ListSubheader,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  createFilterOptions,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import { oportunidadeService } from '@/services/oportunidadeService'
import { Produto, ProdutoCategoria, StatusProduto } from '@/types/produto'
import { AvaliacaoElegibilidadeProduto, ROTULO_TIPO_ELEGIBILIDADE } from '@/types/elegibilidade'
import { formatCurrency } from '@/utils/formatters'
import { categoriaRetirada } from '@/constants/categoriasProduto'
import { CODIGO_OUTRA_NECESSIDADE, rotuloStatusProduto } from '@/constants/codigosProduto'
import { ChipResultadoElegibilidade, ChipRotaOperacao } from '@/components/elegibilidade/ChipElegibilidade'
import { textoPolitica } from '@/components/elegibilidade/formatoElegibilidade'

/**
 * Chave interna de "Outra necessidade" quando o catálogo ainda não traz o produto `OTHER_NEED`
 * (backend anterior à V13 de produtos). Nunca é enviada ao backend: a página manda o id do
 * `OTHER_NEED` ou, na falta dele, `produtoId = null` (AN-PP1 §3.2).
 */
export const OUTRA_NECESSIDADE = 'OUTRA_NECESSIDADE'

/** O produto `OTHER_NEED` do catálogo, se a listagem o trouxe (decide pelo código, nunca pelo nome). */
export const produtoOutraNecessidade = (produtos: Produto[]): Produto | null =>
  produtos.find((p) => p.codigo === CODIGO_OUTRA_NECESSIDADE) ?? null

/** O valor do seletor é "Outra necessidade"? (id do `OTHER_NEED` ou a chave interna). */
export const ehOutraNecessidade = (valor: string, produtos: Produto[]): boolean =>
  valor === OUTRA_NECESSIDADE || (!!valor && valor === produtoOutraNecessidade(produtos)?.id)

/**
 * Chip de status do catálogo (AN-PP1 §2.1) — só os status que mudam algo para o consultor.
 * Não bloqueia nada: "Avaliação Originei" e "Simulação indisponível" são informação.
 */
const AVISO_STATUS: Partial<Record<StatusProduto, string>> = {
  ATIVA_COM_ANALISE: 'A Originei avalia cada operação deste produto antes de seguir com a reserva.',
  SEM_POLITICA_SIMULACAO:
    'Produto sem política comercial de simulação vigente: a reserva pode ser feita, mas a comissão não é simulada até a política ser definida.',
}

const ChipStatus: FC<{ status?: StatusProduto | null }> = ({ status }) => {
  const aviso = status ? AVISO_STATUS[status] : undefined
  if (!status || !aviso) return null
  return (
    <Tooltip title={aviso}>
      <Chip
        size="small"
        variant="outlined"
        color={status === 'SEM_POLITICA_SIMULACAO' ? 'warning' : 'info'}
        label={rotuloStatusProduto(status)}
      />
    </Tooltip>
  )
}

/**
 * Família comercial do produto — o agrupamento que o consultor reconhece ("o cliente
 * precisa antecipar recebíveis"), não a categoria técnica do catálogo.
 */
type Familia =
  | 'ANTECIPACAO'
  | 'CAPITAL_GIRO'
  | 'FINANCIAMENTO_CADEIA'
  | 'GARANTIA_IMOVEL'
  | 'COLABORADORES'
  | 'OUTROS'

const FAMILIA_LABEL: Record<Familia, string> = {
  ANTECIPACAO: 'Antecipação de recebíveis',
  CAPITAL_GIRO: 'Capital de giro',
  FINANCIAMENTO_CADEIA: 'Financiamento de clientes e fornecedores',
  GARANTIA_IMOVEL: 'Crédito com garantia de imóvel',
  COLABORADORES: 'Benefício a colaboradores',
  OUTROS: 'Outros produtos',
}

const ORDEM_FAMILIAS: Familia[] = [
  'ANTECIPACAO', 'CAPITAL_GIRO', 'FINANCIAMENTO_CADEIA', 'GARANTIA_IMOVEL', 'COLABORADORES', 'OUTROS',
]

const FAMILIA_POR_CATEGORIA: Partial<Record<ProdutoCategoria, Familia>> = {
  RISCO_SACADO: 'ANTECIPACAO',
  CESSAO_RECEBIVEIS: 'ANTECIPACAO',
  // DUPLICATA_ESCRITURAL: RETIRADA (Políticas Vigentes 25/10, seção 8) — sem família, e o
  // seletor nem a oferece (ver `oferecidos` no componente).
  SACADO360: 'ANTECIPACAO',
  SACADOR360: 'ANTECIPACAO',
  CAPITAL_GIRO: 'CAPITAL_GIRO',
  CAPITAL_GIRO_CAPEX: 'CAPITAL_GIRO',
  VENDOR_FINANCE: 'FINANCIAMENTO_CADEIA',
  HOME_EQUITY: 'GARANTIA_IMOVEL',
  CONSIGNADO_PRIVADO: 'COLABORADORES',
  COMISSAO_INCENTIVO: 'OUTROS',
}

export const familiaDoProduto = (p: Pick<Produto, 'categoria'>): Familia =>
  FAMILIA_POR_CATEGORIA[p.categoria] ?? 'OUTROS'

/** R$ 200.000.000,00 → "R$ 200 milhões"; demais em moeda. */
export const emReais = (valor: number): string => {
  const milhoes = valor / 1_000_000
  if (milhoes >= 1 && Number.isInteger(milhoes)) {
    return `R$ ${milhoes} ${milhoes === 1 ? 'milhão' : 'milhões'}`
  }
  return formatCurrency(valor)
}

const normalizar = (s: string) =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

interface Props {
  produtos: Produto[]
  /**
   * P2 (AN-PP2 §8): avaliação do SERVIDOR (`POST /v1/oportunidades/elegibilidade/avaliar`, sem
   * `produtoId`) — uma linha por produto. A tela não calcula nada: sem a avaliação (carregando ou
   * rede fora), simplesmente não há chip. Nenhum produto é desabilitado nem rebaixado (Q66).
   */
  avaliacoes?: AvaliacaoElegibilidadeProduto[] | null
  /** Id do produto selecionado, `OUTRA_NECESSIDADE` ou vazio. */
  value: string
  onChange: (id: string) => void
  /**
   * Onda 5 (AN-11 §2.3): recusa do servidor ao criar a reserva (422 `PRODUTO_INATIVO` /
   * `PRODUTO_DESCONHECIDO`), já em texto legível. Aparece acima do campo; a página limpa a
   * escolha e recarrega a lista de ativos.
   */
  aviso?: string | null
}

/** Opção do campo: um produto do catálogo ou "Outra necessidade", sempre a última. */
type Opcao =
  | { tipo: 'produto'; produto: Produto; familia: Familia; avaliacao: AvaliacaoElegibilidadeProduto | null }
  | { tipo: 'outra' }

const OPCAO_OUTRA: Opcao = { tipo: 'outra' }
const ROTULO_OUTRA = 'Outra necessidade'
const GRUPO_OUTRA = 'Não encontrou o produto?'
const TEXTO_OUTRA =
  'Descreva a necessidade do cliente; a Originei identifica a solução. Elegibilidade decidida pela Originei.'

const rotuloDe = (o: Opcao) => (o.tipo === 'outra' ? ROTULO_OUTRA : o.produto.nome)
const grupoDe = (o: Opcao) => (o.tipo === 'outra' ? GRUPO_OUTRA : FAMILIA_LABEL[o.familia])

/** Busca sem acento por nome, descrição e perfil do cliente (só produtos). */
const filtrar = createFilterOptions<Opcao>({
  stringify: (o) =>
    o.tipo === 'outra'
      ? ''
      : normalizar([o.produto.nome, o.produto.descricao ?? '', o.produto.perfilCliente ?? ''].join(' ')),
})

const Campo: FC<{ rotulo: string; children: ReactNode }> = ({ rotulo, children }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" component="div">
      {rotulo}
    </Typography>
    <Typography variant="body2" component="div">
      {children}
    </Typography>
  </Box>
)

/** Primeira mensagem do servidor (para o tooltip do chip e a linha secundária). */
const mensagemDe = (a: AvaliacaoElegibilidadeProduto | null): string | null =>
  a?.explicacoes?.find((e) => e.mensagem)?.mensagem ?? null

/** Chips de elegibilidade da opção/card: resultado + rota, como o servidor devolveu. */
const ChipsElegibilidade: FC<{ avaliacao: AvaliacaoElegibilidadeProduto | null; status?: StatusProduto | null }> = ({
  avaliacao,
  status,
}) => (
  <>
    {avaliacao && <ChipResultadoElegibilidade resultado={avaliacao.resultado} detalhe={mensagemDe(avaliacao)} />}
    {avaliacao && <ChipRotaOperacao rota={avaliacao.rota} />}
    {/* "Avaliação Originei" do status (P1) não repete o mesmo rótulo do resultado MANUAL_REVIEW. */}
    {!(avaliacao?.resultado === 'AVALIACAO_ORIGINEI' && status === 'ATIVA_COM_ANALISE') && <ChipStatus status={status} />}
  </>
)

/**
 * Seletor de produto da Nova Reserva (VC-13): um campo com busca que expande a lista
 * agrupada por família ("Outra necessidade" como opção final). Escolhido o produto, o campo
 * dá lugar a um card de resumo (descrição, elegibilidade, perfil, benefícios, documentos
 * exigidos) com "Trocar produto". Teclado: o Autocomplete do MUI (setas, Enter, Esc).
 *
 * P2 (AN-PP2 §8, Q66): a pré-elegibilidade calculada no navegador SAIU. Os chips vêm do
 * servidor; nenhum produto é desabilitado nem rebaixado na ordem — elegibilidade é alerta no
 * cadastro e só impede o Encaminhamento ao parceiro. Falha de rede = sem chip, nunca bloqueio.
 */
export const SeletorProduto: FC<Props> = ({ produtos, avaliacoes = null, value, onChange, aviso = null }) => {
  const [obrigatorios, setObrigatorios] = useState<number | null>(null)
  /** Depois de "Trocar produto" o campo volta já com o foco (e, pelo openOnFocus, aberto). */
  const [reabrir, setReabrir] = useState(false)

  // P1 (AN-PP1 §3.2): o `OTHER_NEED` sai das opções comuns — é a opção especial "Outra
  // necessidade", que passa a carregar o id dele. Sem ele na listagem, a chave interna.
  const outraProduto = useMemo(() => produtoOutraNecessidade(produtos), [produtos])
  const valorOutra = outraProduto?.id ?? OUTRA_NECESSIDADE

  // Retirada (categoria ou status) nunca é oferecida para reserva nova, mesmo que a listagem a
  // traga. Campanha de Incentivo e CAPEX são produtos comuns do seletor (AN-PP1 §3.1, §5).
  const oferecidos = useMemo(
    () =>
      produtos.filter(
        (p) =>
          !categoriaRetirada(p.categoria) &&
          p.status !== 'RETIRADA' &&
          p.codigo !== CODIGO_OUTRA_NECESSIDADE &&
          p.categoria !== 'OUTRA_NECESSIDADE'
      ),
    [produtos]
  )

  const porProduto = useMemo(() => {
    const mapa = new Map<string, AvaliacaoElegibilidadeProduto>()
    for (const a of avaliacoes ?? []) mapa.set(a.produtoId, a)
    return mapa
  }, [avaliacoes])

  const avaliados = useMemo(
    () =>
      oferecidos.map((p) => ({
        produto: p,
        familia: familiaDoProduto(p),
        avaliacao: porProduto.get(p.id) ?? null,
      })),
    [oferecidos, porProduto]
  )

  // Ordem do catálogo dentro de cada família — o alerta não rebaixa ninguém (§8).
  const opcoes = useMemo<Opcao[]>(() => {
    const porFamilia = ORDEM_FAMILIAS.flatMap((familia) =>
      avaliados.filter((a) => a.familia === familia).map((a): Opcao => ({ tipo: 'produto', ...a }))
    )
    return [...porFamilia, OPCAO_OUTRA]
  }, [avaliados])

  const selecionado = avaliados.find((a) => a.produto.id === value) ?? null
  const outra = value === OUTRA_NECESSIDADE || (!!outraProduto && value === outraProduto.id)

  // Quantos documentos obrigatórios o produto exige — do checklist do produto, no backend.
  useEffect(() => {
    if (!selecionado) {
      setObrigatorios(null)
      return
    }
    let ativo = true
    setObrigatorios(null)
    oportunidadeService
      .exigenciasDoProduto(selecionado.produto.id)
      .then((itens) => {
        if (ativo) setObrigatorios(itens.filter((i) => i.obrigatorio !== false).length)
      })
      .catch(() => {
        // Sem a contagem o card só omite a linha — não é erro do cadastro.
      })
    return () => {
      ativo = false
    }
  }, [selecionado?.produto.id])

  const total = oferecidos.length
  // Contagem do que o servidor marcou como impeditivo no Encaminhamento — não é cálculo de regra.
  const comAlerta = avaliacoes ? avaliados.filter((a) => a.avaliacao?.impedeEncaminhamento).length : null

  const trocar = () => {
    setReabrir(true)
    onChange('')
  }

  const botaoTrocar = (
    <Button size="small" startIcon={<SwapHorizIcon />} onClick={trocar} sx={{ flexShrink: 0, alignSelf: { xs: 'flex-start' } }}>
      Trocar produto
    </Button>
  )

  if (outra) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }} component="section" aria-label="Resumo do produto">
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          justifyContent="space-between"
          alignItems={{ sm: 'center' }}
        >
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {ROTULO_OUTRA}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {TEXTO_OUTRA}
            </Typography>
          </Box>
          {botaoTrocar}
        </Stack>
      </Paper>
    )
  }

  if (selecionado) {
    const p = selecionado.produto
    const a = selecionado.avaliacao
    // P1 (AN-PP1 §6): valor/prazo/taxa do catálogo eram demonstrativos e saíram da política.
    // P2: a regra de elegibilidade é a política versionada — o card mostra o tipo e a versão
    // devolvidos pelo servidor, não as colunas antigas do produto.
    const tipo = a?.politicaTipo ?? p.elegibilidade?.tipo ?? null
    const politica = a ? textoPolitica(a) : null
    return (
      <Paper variant="outlined" sx={{ p: 2 }} component="section" aria-label="Resumo do produto">
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          justifyContent="space-between"
          alignItems={{ sm: 'flex-start' }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" component="div">
              {FAMILIA_LABEL[selecionado.familia]}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {p.nome}
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
              <ChipsElegibilidade avaliacao={a} status={p.status} />
            </Stack>
          </Box>
          {botaoTrocar}
        </Stack>

        {p.descricao && (
          <Typography variant="body2" sx={{ mt: 1.5 }}>
            {p.descricao}
          </Typography>
        )}

        <Box
          sx={{
            mt: 1.5,
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' },
            gap: 1.5,
          }}
        >
          {tipo && <Campo rotulo="Elegibilidade">{ROTULO_TIPO_ELEGIBILIDADE[tipo] ?? tipo}</Campo>}
          {politica && <Campo rotulo="Política">{politica}</Campo>}
          {obrigatorios != null && <Campo rotulo="Documentos obrigatórios">{obrigatorios}</Campo>}
        </Box>

        {p.perfilCliente && (
          <Box sx={{ mt: 1.5 }}>
            <Campo rotulo="Perfil do cliente">{p.perfilCliente}</Campo>
          </Box>
        )}
        {(p.beneficios?.length ?? 0) > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
              Benefícios
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {p.beneficios!.map((b) => (
                <Chip key={b} size="small" variant="outlined" label={b} />
              ))}
            </Stack>
          </Box>
        )}
      </Paper>
    )
  }

  return (
    <Box>
      {aviso && (
        <Alert severity="warning" sx={{ mb: 1.5 }} role="alert">
          {aviso}
        </Alert>
      )}
      <Autocomplete<Opcao, false, false, false>
        options={opcoes}
        value={null}
        onChange={(_, opcao) => {
          if (!opcao) return
          onChange(opcao.tipo === 'outra' ? valorOutra : opcao.produto.id)
        }}
        groupBy={grupoDe}
        getOptionLabel={rotuloDe}
        isOptionEqualToValue={(a, b) => a.tipo === b.tipo && rotuloDe(a) === rotuloDe(b)}
        // A busca filtra só os produtos; "Outra necessidade" fica SEMPRE no fim — é justamente
        // a saída de quem não achou o produto (revisão VAL-R da Onda 2).
        filterOptions={(opts, estado) => [
          ...filtrar(
            opts.filter((o) => o.tipo === 'produto'),
            { ...estado, inputValue: normalizar(estado.inputValue) }
          ),
          OPCAO_OUTRA,
        ]}
        openOnFocus
        autoHighlight
        noOptionsText="Nenhum produto corresponde à busca."
        ListboxProps={{ style: { maxHeight: 420 } }}
        renderGroup={(params) => (
          <li key={params.key}>
            <ListSubheader
              component="div"
              sx={{
                textTransform: 'uppercase',
                fontWeight: 700,
                letterSpacing: '0.06em',
                fontSize: '0.75rem',
                lineHeight: '32px',
              }}
            >
              {params.group}
            </ListSubheader>
            <Box component="ul" sx={{ p: 0 }}>
              {params.children}
            </Box>
          </li>
        )}
        renderOption={(props, opcao) => {
          const { key, ...resto } = props as typeof props & { key: string }
          if (opcao.tipo === 'outra') {
            return (
              <Box component="li" key={key} {...resto} sx={{ display: 'block !important' }}>
                <Typography component="div" sx={{ fontWeight: 600 }}>
                  {ROTULO_OUTRA}
                </Typography>
                <Typography component="div" variant="caption" color="text.secondary">
                  {TEXTO_OUTRA}
                </Typography>
              </Box>
            )
          }
          // Com alerta, a linha secundária é a explicação do servidor (o consultor aprende a
          // regra sem ser impedido); sem alerta, o perfil/descrição do produto.
          const explicacao = opcao.avaliacao?.impedeEncaminhamento ? mensagemDe(opcao.avaliacao) : null
          const secundario = explicacao ?? (opcao.produto.perfilCliente || opcao.produto.descricao || null)
          return (
            <Box component="li" key={key} {...resto} sx={{ display: 'block !important' }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography component="span" sx={{ fontWeight: 600 }}>
                  {opcao.produto.nome}
                </Typography>
                <ChipsElegibilidade avaliacao={opcao.avaliacao} status={opcao.produto.status} />
              </Stack>
              {secundario && (
                <Typography
                  component="div"
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                >
                  {secundario}
                </Typography>
              )}
            </Box>
          )
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Produto"
            placeholder="Buscar produto"
            autoFocus={reabrir}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        )}
      />
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.75 }}>
        {comAlerta != null && comAlerta > 0
          ? `${total} produtos · ${comAlerta} com alerta de elegibilidade`
          : `${total} produtos`}
      </Typography>
    </Box>
  )
}

export default SeletorProduto
